import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader, AlertCircle } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { APP_CONFIG, SUBSCRIPTION_CONSTANTS } from '../config/appConfig';
import logger from '../utils/logger';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userSubscription, refreshSubscription } = useUser();
  const [countdown, setCountdown] = useState(5);
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Verify payment and update subscription
    const verifyPayment = async () => {
      if (!sessionId || !user) {
        setVerifying(false);
        return;
      }

      try {
        const functions = getFunctions();
        const verifyCheckoutSession = httpsCallable(functions, 'verifyCheckoutSession');

        logger.debug('Verifying payment with sessionId:', sessionId);
        const result = await verifyCheckoutSession({ sessionId });

        logger.debug('Verification result:', result.data);

        if (result.data.success && result.data.planId) {
          // The Cloud Function (verifyCheckoutSession) already wrote the subscription
          // to Firestore server-side. Just refresh local state from Firestore.
          logger.info('PaymentSuccess', 'Payment verified, refreshing subscription from Firestore...');
          await refreshSubscription();
          logger.success('PaymentSuccess', 'Subscription refreshed successfully');

          setVerified(true);
        } else {
          logger.error('PaymentSuccess', 'Verification failed or missing planId:', result.data);
          // If explicit failure, show error
          throw new Error('Verification returned failure status');
        }
      } catch (error) {
        logger.error('PaymentSuccess', 'Error verifying payment:', error);
        logger.error('PaymentSuccess', 'Error details:', error.message, error.code, error.details);
        // Ensure we don't show success on actual failure
        setVerified(false);
        // Stop the loader but strictly DON'T show success
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [sessionId, user, refreshSubscription]);

  useEffect(() => {
    // Countdown timer (only start after verification)
    if (!verifying && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (!verifying && countdown === 0) {
      navigate('/');
    }
  }, [verifying, countdown, navigate]);

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-md w-full">
        {/* Success Card */}
        <div className="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700 text-center">
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-6 animate-pulse">
            <CheckCircle size={48} className="text-green-500" />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-extrabold text-white mb-4">
            Payment Successful!
          </h1>

          {/* Message */}
          <p className="text-gray-300 mb-6">
            {verified && userSubscription?.subscriptionPlan
              ? <>Thank you for subscribing to <span className="text-green-400 font-bold">{SUBSCRIPTION_CONSTANTS.getDisplayName(userSubscription.subscriptionPlan)}</span>! Your subscription has been activated.</>
              : 'Thank you! Your subscription has been activated.'
            }
          </p>

          {/* Verification Status */}
          {verifying && (
            <div className="bg-blue-900/20 border border-blue-500/50 rounded-xl p-4 mb-6 flex items-center">
              <Loader className="animate-spin text-blue-400 mr-3" size={20} />
              <p className="text-blue-400 text-sm">Verifying your payment...</p>
            </div>
          )}

          {/* Error Message */}
          {!verifying && !verified && (
            <div className="bg-red-900/20 border border-red-500/50 rounded-xl p-4 mb-6">
              <div className="flex items-center text-red-400 mb-2">
                <AlertCircle size={24} className="mr-2" />
                <h3 className="font-bold">Verification Failed</h3>
              </div>
              <p className="text-red-300 text-sm mb-3">
                We couldn't verify your payment immediately. This might be due to a delay.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-sm w-full transition-colors"
              >
                Retry Verification
              </button>
            </div>
          )}

          {/* Subscription Details - Only show if verified */}
          {!verifying && verified && userSubscription?.subscriptionPlan && (
            <div className="bg-gray-700/50 rounded-xl p-4 mb-6">
              <p className="text-gray-400 text-sm mb-1">Your Plan</p>
              <p className="text-white text-xl font-bold capitalize">
                {SUBSCRIPTION_CONSTANTS.getDisplayName(userSubscription?.subscriptionPlan)}
              </p>
            </div>
          )}

          {/* Session ID (for reference) */}
          {sessionId && (
            <div className="text-xs text-gray-500 mb-6 font-mono">
              Session: {sessionId.substring(0, 20)}...
            </div>
          )}

          {/* Countdown - Only show if verified */}
          {verified && (
            <div className="mb-6">
              <p className="text-gray-400 text-sm mb-2">
                Redirecting to dashboard in
              </p>
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-500/20 rounded-full border-2 border-green-500">
                <span className="text-2xl font-bold text-green-500">{countdown}</span>
              </div>
            </div>
          )}

          {/* Manual Navigation Button */}
          <button
            onClick={() => navigate('/')}
            className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-6 rounded-lg transition-colors flex items-center justify-center group"
          >
            {verified ? 'Go to Dashboard' : 'Return to Dashboard'}
            <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </button>

          {/* Features Unlocked - Only show if verified */}
          {verified && (
            <div className="mt-6 pt-6 border-t border-gray-700">
              <p className="text-green-400 font-semibold mb-3">✨ Features Unlocked</p>
              <div className="space-y-2 text-left text-sm text-gray-300">
                <div className="flex items-center">
                  <CheckCircle size={16} className="text-green-500 mr-2 flex-shrink-0" />
                  <span>Advanced training plans</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle size={16} className="text-green-500 mr-2 flex-shrink-0" />
                  <span>AI biomechanics analysis</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle size={16} className="text-green-500 mr-2 flex-shrink-0" />
                  <span>Priority support</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Help Text */}
        <p className="text-center text-gray-500 text-sm mt-4">
          Need help? Contact us at {APP_CONFIG.supportEmail}
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccess;

